import { createSelector } from '@reduxjs/toolkit';
import { Quote } from './models';
import { baseApi } from '@centerpiece/components';

export const quote = baseApi
  .enhanceEndpoints({ addTagTypes: ['Quotes', 'Quote'] })
  .injectEndpoints({
    endpoints: builder => ({
      getQuotes: builder.query<Quote[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'quote',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'Quote' as const,
                  id,
                })),
                'Quotes',
              ]
            : ['Quotes'],
      }),
      getQuote: builder.query<Quote, { id: number; params?: object }>({
        query: ({ id, params }: { id: number; params?: object }) => ({
          url: `quote/${id}`,
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) => [{ type: 'Quote', id: arg.id }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createQuote: builder.mutation({
        query: request => ({
          url: 'quote',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Quotes'],
      }),
      patchQuote: builder.mutation<Quote, Quote>({
        query: request => ({
          url: `quote/${request.id}`,
          method: 'PATCH',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Quote', id: arg.id },
        ],
      }),
      updateQuote: builder.mutation<Quote, Quote>({
        query: request => ({
          url: `quote/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Quote', id: arg.id },
        ],
      }),
      deleteQuote: builder.mutation<Quote, number>({
        query: id => ({
          url: `quote/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result ? [{ type: 'Quote' as const, arg }, 'Quotes'] : ['Quotes'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetQuotesQuery,
  useGetQuoteQuery,
  useCreateQuoteMutation,
  usePatchQuoteMutation,
  useUpdateQuoteMutation,
  useDeleteQuoteMutation,
  usePrefetch: useQuotesPrefetch,
} = quote;
const selectQuotesRoot = quote.endpoints.getQuotes.select({});
export const selectQuotes = createSelector(
  selectQuotesRoot,
  quote => quote.data || []
);
